import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import Webcam from 'react-webcam';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Camera, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * FaceCapture — webcam capture for:
 * - Registration flow (manual capture)
 * - Scan flow face mode (auto-capture after a short warm-up, plus manual capture)
 *
 * Face recognition/validation is performed on the BACKEND (AWS Rekognition). This component
 * only grabs a JPEG frame and hands the base64 to the parent via onCapture — there is no
 * in-browser face detection.
 */

// ponytail: fixed camera warm-up before auto-capturing in scan mode. There's no client-side
// face detector anymore, so we can't wait for a "good" frame; the backend rejects unusable
// frames and the operator can re-scan. Upgrade path: add a lightweight presence heuristic.
const AUTO_CAPTURE_DELAY_MS = 2500;

const FaceCapture = ({
  onCapture,
  onError,
  className = '',
  mode = 'registration', // 'registration' | 'scan'
  autoCapture = false, // when true (scan flow), capture automatically after warm-up
  isActive = true, // when false, camera is idle (used for face scan start/stop)
}) => {
  const webcamRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);
  const [permissionError, setPermissionError] = useState(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [usingFallbackCamera, setUsingFallbackCamera] = useState(false);

  // Primary: front camera, ideal 720x720; fallback: any camera, ideal 720x720.
  const videoConstraintsPrimary = useMemo(
    () => ({
      width: { ideal: 720 },
      height: { ideal: 720 },
      facingMode: { ideal: 'user' },
    }),
    []
  );

  const videoConstraintsFallback = useMemo(
    () => ({
      width: { ideal: 720 },
      height: { ideal: 720 },
    }),
    []
  );

  const [activeConstraints, setActiveConstraints] = useState(videoConstraintsPrimary);

  // When the parent turns this capture view off (e.g. after one scan),
  // reset transient UI state so the next activation starts clean.
  useEffect(() => {
    if (!isActive) {
      setCapturedImage(null);
      setError(null);
      setPermissionError(null);
      setIsCameraReady(false);
    }
  }, [isActive]);

  const handleCapture = useCallback(() => {
    try {
      const imageSrc = webcamRef.current?.getScreenshot();
      if (!imageSrc) {
        throw new Error('Failed to capture image');
      }

      // In registration mode we freeze the frame for review/retake.
      // In scan mode we keep the live camera running so multiple faces
      // can be scanned one after another.
      if (mode === 'registration') {
        setCapturedImage(imageSrc);
      }
      setError(null);

      if (onCapture) {
        // JPEG base64 (quality ~0.85) as expected by the backend recognition endpoint.
        onCapture(imageSrc);
      }
    } catch (err) {
      console.error('Face capture failed:', err);
      const friendlyMessage =
        'Failed to capture image. You can try again, or skip face capture and continue.';
      setError(friendlyMessage);
      onError?.(err);
    }
  }, [onCapture, onError, mode]);

  // Scan mode: auto-capture a single frame shortly after the camera is ready.
  useEffect(() => {
    if (mode !== 'scan' || !autoCapture) return undefined;
    if (!isActive || !isCameraReady || capturedImage || permissionError) return undefined;

    const timer = setTimeout(() => handleCapture(), AUTO_CAPTURE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [mode, autoCapture, isActive, isCameraReady, capturedImage, permissionError, handleCapture]);

  const handleRetry = () => {
    setCapturedImage(null);
    setError(null);
  };

  const handleWebcamError = useCallback(
    (err) => {
      console.error('Webcam error:', err);

      // If primary (front) camera fails due to constraints, gracefully fall back
      if (!usingFallbackCamera) {
        setUsingFallbackCamera(true);
        setActiveConstraints(videoConstraintsFallback);
      }

      let message =
        'Failed to access camera. Please check permissions or try a different device. You can still continue without face capture.';

      if (err?.name === 'NotAllowedError') {
        message =
          'Camera access denied. Please allow camera permissions in your browser settings. You can continue registration without face capture.';
      } else if (err?.name === 'NotFoundError') {
        message =
          'No camera found on this device. You can proceed without face capture.';
      }

      setPermissionError(message);
      setError(message);
      onError?.(err);
    },
    [onError, usingFallbackCamera, videoConstraintsFallback]
  );

  const handleUserMedia = useCallback(() => {
    setIsCameraReady(true);
    setPermissionError(null);
  }, []);

  const instructions =
    mode === 'registration'
      ? [
          'Align your face inside the frame',
          'Remove glasses, mask, or cap if possible',
          'Face registration is optional – you can skip this step',
        ]
      : [
          'Align your face inside the frame',
          'Hold still while we capture and match on the server',
          'Remove glasses, mask, or cap if possible',
        ];

  const canCapture = isCameraReady && !permissionError;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Camera className="w-5 h-5" />
          <span>{mode === 'registration' ? 'Face Capture' : 'Face Scan'}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Error / permission issues */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-red-700">
                <AlertCircle className="w-5 h-5" />
                <p>{error}</p>
              </div>
            </div>
          )}

          {/* Captured preview */}
          {!error && capturedImage ? (
            <div className="space-y-4">
              <div className="relative">
                <img
                  src={capturedImage}
                  alt="Captured face"
                  className="w-full rounded-lg"
                />
                <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                  <CheckCircle className="w-5 h-5" />
                </div>
              </div>
              {mode === 'registration' && (
                <Button
                  onClick={handleRetry}
                  variant="outline"
                  className="w-full"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retake Photo
                </Button>
              )}
            </div>
          ) : null}

          {/* Live camera view (or idle placeholder in scan mode) */}
          {!capturedImage && (
            <div className="space-y-4">
              {mode === 'scan' && !isActive ? (
                // Idle placeholder: no camera access until operator clicks "Start Face Scan"
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center max-w-md mx-auto">
                  <Camera className="w-6 h-6 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-700 font-medium">
                    Click &quot;Start Face Scan&quot; to open the camera.
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    The camera will open and capture your face automatically for server-side matching.
                  </p>
                </div>
              ) : (
                <div className="relative rounded-lg overflow-hidden bg-black max-w-md mx-auto">
                  {(mode !== 'scan' || isActive) && (
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      screenshotQuality={0.85}
                      videoConstraints={activeConstraints}
                      onUserMediaError={handleWebcamError}
                      onUserMedia={handleUserMedia}
                      className="w-full aspect-square object-cover"
                      mirrored // front camera UX
                    />
                  )}

                  {/* Face alignment overlay: centered oval with darkened surroundings.
                      NOTE: pointer-events-none so it never blocks taps/clicks. */}
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    {/* Darkened background */}
                    <div className="absolute inset-0 bg-black/60" />

                    {/* Transparent oval "hole" using border + blend trick */}
                    <div className="relative w-3/4 max-w-xs aspect-[3/4]">
                      <div className="absolute inset-0 rounded-full border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.6)]" />
                    </div>
                  </div>

                  {/* Lightweight status chip (bottom) */}
                  <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center">
                    <div className="px-3 py-1 rounded-full text-xs font-medium bg-black/60 text-gray-100">
                      {!isCameraReady
                        ? 'Initializing camera...'
                        : mode === 'scan'
                        ? 'Position your face in the oval — capturing automatically...'
                        : 'Align your face inside the frame'}
                    </div>
                  </div>
                </div>
              )}

              {/* Manual capture button */}
              {(mode === 'registration' || (mode === 'scan' && isActive)) && (
                <Button
                  onClick={handleCapture}
                  className="w-full"
                  disabled={!canCapture}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {!isCameraReady
                    ? 'Initializing Camera...'
                    : mode === 'scan'
                    ? 'Capture Now'
                    : 'Capture Photo'}
                </Button>
              )}
            </div>
          )}

          {/* Registration safety note */}
          {mode === 'registration' && (
            <div className="text-xs text-gray-500 space-y-1">
              <p className="font-medium">Tips for better detection:</p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                {instructions.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <p className="mt-2">
                Face registration is optional. Your registration will still be completed even
                if face capture is skipped or fails.
              </p>
            </div>
          )}

          {/* Scan mode instructions */}
          {mode === 'scan' && (
            <div className="text-xs text-gray-500 space-y-1">
              <p className="font-medium">Face scan instructions</p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                {instructions.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FaceCapture;
